import { motion } from "framer-motion";
import { RotateCcw, CheckCircle, XCircle, Package, DollarSign, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

export default function RefundPolicyPage() {
  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <RotateCcw className="w-20 h-20 mx-auto mb-6" />
            <h1 className="text-6xl font-black mb-6">Return & Refund Policy</h1>
            <p className="text-2xl text-purple-100 max-w-3xl mx-auto">
              30-Day Return Policy • Customer Satisfaction Guaranteed
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-8"
        >
          {/* Return Policy Overview */}
          <Card className="border-2 border-purple-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <RotateCcw className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-3xl font-black text-gray-900 mb-4">30-Day Return Policy</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    We want you to be completely satisfied with your LCTX Tyre Changer purchase. You can return your product within <strong>30 days from the delivery date</strong> if you're not satisfied for any reason.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    This policy complies with Stripe's payment protection and consumer rights standards.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Return Eligibility */}
          <Card className="border-2 border-green-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">Eligible for Return</h2>
                  <div className="space-y-3">
                    {[
                      "Product returned within 30 days from delivery date",
                      "Item in original, unused condition with no signs of use",
                      "All original packaging, accessories, and manuals included",
                      "Product has not been modified, altered, or damaged",
                      "Proof of purchase (order confirmation email) provided",
                      "Product serial number matches original purchase"
                    ].map((item, index) => (
                      <div key={index} className="flex items-start gap-3 bg-green-50 rounded-lg p-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Non-Returnable Items */}
          <Card className="border-2 border-red-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">Not Eligible for Return</h2>
                  <div className="space-y-3">
                    {[
                      "Returns requested after 30 days from delivery date",
                      "Used, damaged, or incomplete products",
                      "Items missing original packaging, parts, or accessories",
                      "Products with signs of wear, tear, or usage",
                      "Modified, altered, or customized units",
                      "Damage caused by misuse, improper installation, or accidents",
                      "Products with removed or damaged serial numbers"
                    ].map((item, index) => (
                      <div key={index} className="flex items-start gap-3 bg-red-50 rounded-lg p-3">
                        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Return Shipping Cost */}
          <Card className="border-2 border-orange-200 shadow-xl bg-orange-50">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Package className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">📦 Return Shipping Policy</h2>
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-5 border-2 border-orange-300">
                      <h3 className="font-bold text-orange-900 text-xl mb-3">🚚 Customer Pays Return Shipping</h3>
                      <p className="text-gray-700 mb-3">
                        <strong>For change of mind returns:</strong> You are responsible for all return shipping costs. We recommend using a trackable shipping service and purchasing shipping insurance.
                      </p>
                      <p className="text-gray-700">
                        Return shipping typically costs <strong>$50-$150 USD</strong> depending on your location and chosen carrier.
                      </p>
                    </div>

                    <div className="bg-green-50 rounded-lg p-5 border-2 border-green-300">
                      <h3 className="font-bold text-green-900 text-xl mb-3">✅ We Cover Return Shipping If:</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Product arrives defective or damaged</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>We sent you the wrong item</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Product has manufacturing defects within warranty period</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* How to Return */}
          <Card className="border-2 border-blue-200 shadow-xl">
            <CardContent className="p-8">
              <h2 className="text-3xl font-black text-gray-900 mb-6">📋 How to Return Your Product</h2>
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-5 border-l-4 border-blue-600">
                  <h3 className="font-bold text-blue-900 text-xl mb-2">Step 1: Contact Us Within 30 Days</h3>
                  <p className="text-gray-700">
                    Email <strong>hkjenholtd@gmail.com</strong> with your order number, reason for return, and photos of the product. We'll respond within 24 hours.
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    ⏰ <strong>Important:</strong> Returns must be initiated within 30 days from delivery date, not order date.
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-5 border-l-4 border-blue-600">
                  <h3 className="font-bold text-blue-900 text-xl mb-2">Step 2: Get Return Authorization</h3>
                  <p className="text-gray-700">
                    We'll review your request and provide a Return Authorization (RA) number and return shipping instructions within 1-2 business days.
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-5 border-l-4 border-blue-600">
                  <h3 className="font-bold text-blue-900 text-xl mb-2">Step 3: Prepare Your Return Package</h3>
                  <p className="text-gray-700 mb-2">
                    Carefully repack the product in its original packaging with:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>All accessories, manuals, and parts</li>
                    <li>Original box and protective materials</li>
                    <li>RA number clearly written on the outside</li>
                    <li>Copy of your purchase receipt inside</li>
                  </ul>
                </div>

                <div className="bg-blue-50 rounded-lg p-5 border-l-4 border-blue-600">
                  <h3 className="font-bold text-blue-900 text-xl mb-2">Step 4: Ship the Return</h3>
                  <p className="text-gray-700 mb-2">
                    Ship to the address provided in your RA confirmation. <strong>You are responsible for return shipping costs</strong> unless the product is defective or we made an error.
                  </p>
                  <p className="text-orange-700 font-semibold text-sm mt-2">
                    ⚠️ Keep your tracking number! We're not responsible for items lost in return transit.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Refund Process */}
          <Card className="border-2 border-green-200 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-8 h-8 text-green-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">💰 Refund Processing Timeline</h2>
                  <div className="space-y-4">
                    <div className="bg-green-50 rounded-lg p-4">
                      <h3 className="font-bold text-green-900 mb-2">🔍 Inspection (2-3 business days)</h3>
                      <p className="text-gray-700">
                        Once we receive your return, we'll inspect it to ensure it meets our return criteria. We'll email you updates during this process.
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <h3 className="font-bold text-green-900 mb-2">✅ Refund Approval (1-2 business days)</h3>
                      <p className="text-gray-700">
                        If approved, we'll process your refund to your original payment method within 1-2 business days.
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <h3 className="font-bold text-green-900 mb-2">💳 Refund Appears in Your Account (5-10 business days)</h3>
                      <p className="text-gray-700">
                        Depending on your bank or credit card company, it may take 5-10 business days for the refund to appear in your account.
                      </p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4 border-2 border-yellow-300">
                      <h3 className="font-bold text-yellow-900 mb-2">📉 Deductions from Refund</h3>
                      <p className="text-gray-700">
                        Your refund will be the full purchase price minus any applicable restocking fees (if the item is not in original condition). Original shipping costs are non-refundable for change-of-mind returns.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Important Notes */}
          <Card className="bg-red-50 border-2 border-red-300 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-12 h-12 text-red-600 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-black text-red-900 mb-4">⚠️ Important Information</h2>
                  <div className="space-y-3 text-gray-800">
                    <p className="flex items-start gap-2">
                      <span className="font-bold text-red-600">•</span>
                      <span><strong>30-day period starts from delivery date,</strong> not order date. Check your delivery confirmation email for the exact date.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="font-bold text-red-600">•</span>
                      <span><strong>Customer pays return shipping</strong> for change-of-mind returns. Plan for $50-$150 USD shipping costs.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="font-bold text-red-600">•</span>
                      <span><strong>Items must be unused and in original packaging.</strong> Used or damaged items will be rejected or subject to restocking fees.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="font-bold text-red-600">•</span>
                      <span><strong>Keep tracking information!</strong> We cannot process returns without valid tracking and proof of shipment.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="font-bold text-red-600">•</span>
                      <span><strong>Original shipping costs are non-refundable</strong> for change-of-mind returns.</span>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Section */}
          <Card className="bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-xl">
            <CardContent className="p-8 text-center">
              <h2 className="text-3xl font-black mb-4">Questions About Returns?</h2>
              <p className="text-xl text-purple-100 mb-6">
                Our customer service team is here to help make your return process smooth and easy.
              </p>
              <div className="space-y-3">
                <p className="text-2xl font-bold">
                  📧 hkjenholtd@gmail.com
                </p>
                <p className="text-xl">
                  📞 +852-63613177
                </p>
                <p className="text-purple-100 mt-4">
                  Response time: Within 24 hours • Sunday–Thursday, 9AM-5PM GST (UTC+4)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Stripe Compliance Notice */}
          <div className="bg-gray-100 rounded-xl p-6 text-center">
            <p className="text-sm text-gray-600">
              This return policy complies with Stripe's payment protection standards and applicable consumer protection laws in the UAE and Saudi Arabia. 
              All refunds are processed securely through Stripe's payment gateway.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}